/**
 * @typedef {object} DirectEditCellPatch
 * @property {Partial<ApiManagerTableCellValueContentObject>} [value] - Partial patch on cell.value (shallow merge)
 * @property {string | null} [backgroundColor] - Cell background color override (e.g. badge color for selectClass)
 */

/**
 * Build the patched cell from a snapshot cell and a patch.
 * Shallow merge on the cell, with a one-level shallow merge on `value`.
 * Falls back to legacy behavior (overwrite `value.string`) when no patch is provided.
 * @param {ApiManagerTableCellValueObject} cell - The cell to patch
 * @param {DirectEditCellPatch | undefined} patch - The patch to apply
 * @param {string} fallbackString - Fallback string value when patch is undefined
 * @returns {ApiManagerTableCellValueObject} The patched cell
 */
export const patchDirectEditCell = (cell, patch, fallbackString) => {
  if (!patch) {
    return {
      ...cell,
      value: {
        ...cell.value,
        string: fallbackString,
      },
    };
  }
  return {
    ...cell,
    ...('backgroundColor' in patch ? { backgroundColor: patch.backgroundColor } : {}),
    value: {
      ...cell.value,
      ...(patch.value ?? {}),
    },
  };
};

/**
 * Apply a pending-save patch to a single cell in the table data snapshot.
 * @param {ApiManagerTableResponse} tableData - The table data to patch
 * @param {string} rowId - The row id of the cell to patch
 * @param {string} columnId - The column id of the cell to patch
 * @param {DirectEditCellPatch | undefined} patch - Patch to apply
 * @param {string} fallbackString - Fallback string value when patch is undefined
 * @returns {ApiManagerTableResponse} The patched table data (new reference)
 */
export const applyDirectEditCellPatchToTableData = (
  tableData,
  rowId,
  columnId,
  patch,
  fallbackString,
) => ({
  ...tableData,
  groups: tableData.groups.map((group) => ({
    ...group,
    data: group.data.map((row) => {
      if (row.id !== rowId) return row;
      return {
        ...row,
        cells: row.cells.map((cell) => {
          if (cell.columnId !== columnId) return cell;
          return patchDirectEditCell(cell, patch, fallbackString);
        }),
      };
    }),
  })),
});
